// GPS Service for LIMA Automotive API integration
// Provides geocoding, reverse geocoding, and address autocomplete services

const LIMA_API_BASE_URL = 'https://limaapi.onrender.com';

export interface GpsCoordinates {
  latitude: number;
  longitude: number;
}

export interface AddressSuggestion {
  label: string;
  address: string;
  country: string;
  region: string;
  latitude: number;
  longitude: number;
  currency: string;
}

export interface PortLocation {
  id: string;
  name: string;
  address: string;
  region: string;
  coordinates: GpsCoordinates;
}

export interface NearestPortResponse {
  location: {
    address: string;
    coordinates: GpsCoordinates;
    country_code: string;
    currency: string;
  };
  nearest_port: PortLocation;
  distance: {
    kilometers: number;
    miles: number;
  };
  port_region: string;
}

export interface CurrencyInfo {
  currencies: string[];
  count: number;
}

export interface ExchangeRateResponse {
  amount: number;
  from_currency: string;
  to_currency: string;
  converted_amount: number;
  exchange_rate: number;
}

export interface RatesResponse {
  base_currency: string;
  rates: Record<string, number>;
  timestamp: number;
}

/**
 * Convert address to GPS coordinates using LIMA Automotive API
 * @param address - The address to geocode
 * @returns Promise with coordinates or null if failed
 */
export const geocodeAddress = async (address: string): Promise<GpsCoordinates | null> => {
  try {
    const response = await fetch(`${LIMA_API_BASE_URL}/gps`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ address }),
    });

    if (!response.ok) {
      const error = await response.json();
      console.error('Geocoding error:', error);
      return null;
    }

    const data = await response.json();
    return {
      latitude: data.latitude,
      longitude: data.longitude,
    };
  } catch (error) {
    console.error('Geocoding request failed:', error);
    return null;
  }
};

/**
 * Get address suggestions for autocomplete using LIMA Automotive API
 * @param query - Search query
 * @param limit - Maximum number of suggestions (default: 5)
 * @returns Promise with address suggestions
 */
export const getAddressSuggestions = async (
  query: string,
  limit: number = 5
): Promise<AddressSuggestion[]> => {
  try {
    if (query.length < 2) return [];

    const response = await fetch(
      `${LIMA_API_BASE_URL}/autocomplete?q=${encodeURIComponent(query)}&limit=${limit}`
    );

    if (!response.ok) {
      const error = await response.json();
      console.error('Address suggestions error:', error);
      return [];
    }

    const data = await response.json();
    return data.suggestions || [];
  } catch (error) {
    console.error('Address suggestions request failed:', error);
    return [];
  }
};

/**
 * Find the nearest port to a given location using LIMA Automotive API
 * @param coordinates - Location coordinates
 * @returns Promise with nearest port information
 */
export const findNearestPort = async (
  coordinates: GpsCoordinates
): Promise<NearestPortResponse | null> => {
  try {
    const response = await fetch(`${LIMA_API_BASE_URL}/nearest-port`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        location: coordinates,
        type: 'pointReq',
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      console.error('Nearest port error:', error);
      return null;
    }

    return await response.json();
  } catch (error) {
    console.error('Nearest port request failed:', error);
    return null;
  }
};

/**
 * Get available currencies from LIMA Automotive API
 * @returns Promise with list of currencies
 */
export const getAvailableCurrencies = async (): Promise<CurrencyInfo | null> => {
  try {
    const response = await fetch(`${LIMA_API_BASE_URL}/currencies`);

    if (!response.ok) {
      const error = await response.json();
      console.error('Currencies error:', error);
      return null;
    }

    return await response.json();
  } catch (error) {
    console.error('Currencies request failed:', error);
    return null;
  }
};

/**
 * Convert amount between currencies using LIMA Automotive API
 * @param amount - Amount to convert
 * @param fromCurrency - Source currency
 * @param toCurrency - Target currency
 * @returns Promise with conversion result
 */
export const convertCurrency = async (
  amount: number,
  fromCurrency: string,
  toCurrency: string
): Promise<ExchangeRateResponse | null> => {
  try {
    const response = await fetch(`${LIMA_API_BASE_URL}/convert`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        amount,
        from_currency: fromCurrency,
        to_currency: toCurrency,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      console.error('Currency conversion error:', error);
      return null;
    }

    return await response.json();
  } catch (error) {
    console.error('Currency conversion request failed:', error);
    return null;
  }
};

/**
 * Get current exchange rates from LIMA Automotive API
 * @returns Promise with exchange rates
 */
export const getExchangeRates = async (): Promise<RatesResponse | null> => {
  try {
    const response = await fetch(`${LIMA_API_BASE_URL}/rates`);

    if (!response.ok) {
      const error = await response.json();
      console.error('Exchange rates error:', error);
      return null;
    }

    return await response.json();
  } catch (error) {
    console.error('Exchange rates request failed:', error);
    return null;
  }
};

/**
 * Check API health status
 * @returns Promise with health status
 */
export const checkApiHealth = async (): Promise<any> => {
  try {
    const response = await fetch(`${LIMA_API_BASE_URL}/health`);

    if (!response.ok) {
      const error = await response.json();
      console.error('Health check error:', error);
      return null;
    }

    return await response.json();
  } catch (error) {
    console.error('Health check request failed:', error);
    return null;
  }
};

/**
 * Calculate distance between two coordinates using Haversine formula
 * @param coord1 - First coordinate
 * @param coord2 - Second coordinate
 * @returns Distance in kilometers
 */
export const calculateDistance = (
  coord1: GpsCoordinates,
  coord2: GpsCoordinates
): number => {
  const R = 6371; // Earth's radius in kilometers
  const dLat = (coord2.latitude - coord1.latitude) * (Math.PI / 180);
  const dLon = (coord2.longitude - coord1.longitude) * (Math.PI / 180);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(coord1.latitude * (Math.PI / 180)) *
      Math.cos(coord2.latitude * (Math.PI / 180)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

/**
 * Format coordinates for display
 * @param coordinates - GPS coordinates
 * @param precision - Decimal places (default: 6)
 * @returns Formatted coordinate string
 */
export const formatCoordinates = (
  coordinates: GpsCoordinates,
  precision: number = 6
): string => {
  const lat = coordinates.latitude.toFixed(precision);
  const lon = coordinates.longitude.toFixed(precision);
  return `${lat}, ${lon}`;
};

/**
 * Validate if coordinates are within valid ranges
 * @param coordinates - GPS coordinates to validate
 * @returns True if coordinates are valid
 */
export const validateCoordinates = (coordinates: GpsCoordinates): boolean => {
  return (
    coordinates.latitude >= -90 &&
    coordinates.latitude <= 90 &&
    coordinates.longitude >= -180 &&
    coordinates.longitude <= 180
  );
};
